import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getMyPermissions } from '@/utils/supabase/adminManagement';
import { EMPTY_PERMISSION_GRID, isAnyAccessGranted, type PermissionGrid } from '@/utils/roleBasedAccess';

interface AdminAuthContextType {
  isAdminAuthenticated: boolean;
  permissions: PermissionGrid;
  adminEmail: string | null;
  setAdminAuthenticated: (value: boolean) => void;
  logout: () => Promise<void>;
  isLoading: boolean;
  refreshAuth: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType>({
  isAdminAuthenticated: false,
  permissions: EMPTY_PERMISSION_GRID,
  adminEmail: null,
  setAdminAuthenticated: () => {},
  logout: async () => {},
  isLoading: true,
  refreshAuth: async () => {},
});

export const useAdminAuth = () => useContext(AdminAuthContext);

interface AuthUser {
  email?: string | null;
}

// Throws on lookup failure (network/RPC error) instead of swallowing it — callers
// decide whether a failed check should reset auth state or keep the current one.
async function checkAdminStatus(user: AuthUser, signal?: AbortSignal): Promise<{ isAdmin: boolean; permissions: PermissionGrid; email: string | null }> {
  if (!user) return { isAdmin: false, permissions: EMPTY_PERMISSION_GRID, email: null };
  const permissions = await getMyPermissions(signal);
  const isAdmin = isAnyAccessGranted(permissions);
  return { isAdmin, permissions, email: isAdmin ? (user.email ?? null) : null };
}

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<PermissionGrid>(EMPTY_PERMISSION_GRID);
  const [adminEmail, setAdminEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const initializingRef = useRef(false);
  const processingAuthChangeRef = useRef(false);
  const pendingAuthEventRef = useRef<{ event: string; session: Session | null } | null>(null);
  const authSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  const updateAuthState = (isAuthenticated: boolean, grid: PermissionGrid, email: string | null = null) => {
    setIsAdminAuthenticated(isAuthenticated);
    setPermissions(grid);
    setAdminEmail(email);
  };

  const refreshAuth = useCallback(async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) { updateAuthState(false, EMPTY_PERMISSION_GRID); return; }
      if (session?.user) {
        const { isAdmin, permissions: grid, email } = await checkAdminStatus(session.user);
        updateAuthState(isAdmin, grid, email);
      } else {
        updateAuthState(false, EMPTY_PERMISSION_GRID);
      }
    } catch {
      updateAuthState(false, EMPTY_PERMISSION_GRID);
    }
  }, []);

  useEffect(() => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    const processAuthEvent = async (event: string, session: any) => {
      processingAuthChangeRef.current = true;
      try {
        if (event === 'SIGNED_IN' && session?.user) {
          try {
            const { isAdmin, permissions: grid, email } = await checkAdminStatus(session.user);
            updateAuthState(isAdmin, grid, email);
          } catch {
            updateAuthState(false, EMPTY_PERMISSION_GRID);
          }
        } else if (event === 'SIGNED_OUT') {
          updateAuthState(false, EMPTY_PERMISSION_GRID);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Re-check on every refresh (not just while unauthenticated) so a
          // permission-grid change is picked up without a full reload.
          try {
            const { isAdmin, permissions: grid, email } = await checkAdminStatus(session.user);
            updateAuthState(isAdmin, grid, email);
          } catch {
            // Transient lookup failure — keep the existing session state rather
            // than signing out an already-authenticated admin.
          }
        }
      } finally {
        processingAuthChangeRef.current = false;
        if (pendingAuthEventRef.current) {
          const pending = pendingAuthEventRef.current;
          pendingAuthEventRef.current = null;
          setTimeout(() => processAuthEvent(pending.event, pending.session), 0);
        }
      }
    };

    const setupAuthListener = () => {
      // Callback must stay synchronous (no async/await): onAuthStateChange runs it inside
      // supabase-js's exclusive auth lock, and processAuthEvent's checkAdminStatus call
      // re-enters that same lock (via getUser()) — awaiting it here would self-deadlock,
      // permanently blocking every other getSession()/getUser() call on the page.
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        if (initializingRef.current) {
          pendingAuthEventRef.current = { event, session };
          return;
        }
        if (processingAuthChangeRef.current) {
          pendingAuthEventRef.current = { event, session };
          return;
        }
        void processAuthEvent(event, session);
      });
      authSubscriptionRef.current = subscription;
    };

    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) { updateAuthState(false, EMPTY_PERMISSION_GRID); return; }
        if (session?.user) {
          const { isAdmin, permissions: grid, email } = await checkAdminStatus(session.user);
          updateAuthState(isAdmin, grid, email);
        } else {
          updateAuthState(false, EMPTY_PERMISSION_GRID);
        }
      } catch {
        updateAuthState(false, EMPTY_PERMISSION_GRID);
      } finally {
        setIsLoading(false);
        const pending = pendingAuthEventRef.current;
        pendingAuthEventRef.current = null;
        initializingRef.current = false;
        if (pending) {
          setTimeout(() => processAuthEvent(pending.event, pending.session), 0);
        }
      }
    };

    setupAuthListener();

    const timeoutId = setTimeout(() => {
      if (initializingRef.current) {
        setIsLoading(false);
        initializingRef.current = false;
      }
    }, 8000);

    initializeAuth().finally(() => clearTimeout(timeoutId));

    return () => {
      clearTimeout(timeoutId);
      if (authSubscriptionRef.current) {
        authSubscriptionRef.current.unsubscribe();
      }
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      updateAuthState(false, EMPTY_PERMISSION_GRID);
    } catch {
      updateAuthState(false, EMPTY_PERMISSION_GRID);
    }
  }, []);

  const setAdminAuthenticated = (value: boolean) => {
    if (value) return;
    updateAuthState(false, EMPTY_PERMISSION_GRID);
  };

  const contextValue = useMemo(() => ({
    isAdminAuthenticated,
    permissions,
    adminEmail,
    setAdminAuthenticated,
    logout,
    isLoading,
    refreshAuth
  }), [isAdminAuthenticated, permissions, adminEmail, isLoading, logout, refreshAuth]);

  return (
    <AdminAuthContext.Provider value={contextValue}>
      {children}
    </AdminAuthContext.Provider>
  );
};
